import { Component, EventEmitter, Output, ViewChild } from "@angular/core";
import { SlickDialogComponent } from "@slick-components";
import { MaintenanceHistoryService, ReadingsAndDosagesService, StructureTypeService } from "@services";
import {
	IMaintenanceReadingModel, MaintenanceReadingModel, MaintenanceReadingValueModel,
	IDosageModel, DosageModel, DosageValueModel, IStructureModel, IStructureTypeModel
} from "@models";

interface IReadingRow {
	maintenanceReadingItemId: number;
	itemName: string;
	uom: string;
	values: number[];
	itemValue: string;
}

interface IDosageRow {
	dosageItemId: number;
	description: string;
	uom: string;
	values: number[];
	value: number;
	addToInvoice: boolean;
	// "Add to invoice" only applies when the dosage type is linked to a billable item.
	hasItem: boolean;
}

@Component({
	selector: "readings-and-dosages-dialog",
	templateUrl: "readings-and-dosages-dialog.component.html",
	styleUrls: ["readings-and-dosages-dialog.component.scss"],
	providers: [MaintenanceHistoryService, ReadingsAndDosagesService]
})
export class ReadingsAndDosagesDialogComponent {
	@ViewChild("dialogRef", { static: true }) dialogRef: SlickDialogComponent;

	// So the Assets tab can refresh the pool's history once something is recorded on it.
	@Output() onSaved: EventEmitter<void> = new EventEmitter<void>();

	customerId: number;
	appointmentId: number;
	spinnerStatus: string;

	// Which pool/spa this was taken on. Picked in the dialog when it's opened from an appointment
	// (the tech is at a site, not on one asset); fixed by the caller when it's opened from Assets.
	structureId: number = null;
	structures: IStructureModel[] = [];
	structureTypes: IStructureTypeModel[] = [];
	showStructurePicker: boolean = false;

	readingRows: IReadingRow[] = [];
	dosageRows: IDosageRow[] = [];

	// Tracks which value input's suggestion panel is open. We render our own panel to the
	// side of the input instead of the browser's native datalist (which drops below and
	// covers the rows underneath).
	activePanel: { kind: "reading" | "dosage", index: number } = null;
	private closePanelTimeout: any = null;

	private maintenanceReading: IMaintenanceReadingModel;
	private dosage: IDosageModel;

	constructor(
		private readonly maintenanceHistoryService: MaintenanceHistoryService,
		private readonly readingsAndDosagesService: ReadingsAndDosagesService,
		private readonly structureTypeService: StructureTypeService) { }

	// appointmentId 0/null opens the dialog on a brand new record, which is how the Assets tab adds
	// readings against a pool with no visit behind them. structureId fixes the pool when the caller
	// already knows it; left null the dialog offers a picker instead.
	async openDialog(customerId: number, appointmentId: number, structureId: number = null) {
		this.customerId = customerId;
		this.appointmentId = appointmentId || null;
		this.structureId = structureId;
		this.spinnerStatus = "reset";

		const readingItems = (await this.readingsAndDosagesService.getReadingItems())
			.filter(x => x.isActive && !x.isDeleted)
			.sort((a, b) => a.sortOrder - b.sortOrder);

		const dosageItems = (await this.maintenanceHistoryService.getDosageItems())
			.filter(x => x.isActive)
			.sort((a, b) => a.sortOrder - b.sortOrder);

		// Only offered when the caller didn't already fix the pool. An asset with nothing set up yet
		// still saves, just with no structure attached.
		this.showStructurePicker = (structureId === null || structureId === undefined);
		if (this.showStructurePicker) {
			this.structureTypes = await this.structureTypeService.getStructureTypes();
			this.structures = await this.structureTypeService.getStructures(customerId);
		}

		// Load anything already recorded against this appointment so the dialog edits it rather than duplicating.
		if (this.appointmentId) {
			const summary = await this.maintenanceHistoryService.getMaintenanceForAppointment(customerId, this.appointmentId);
			this.maintenanceReading = (summary.maintenanceReadings || []).find(x => x.appointmentId === this.appointmentId);
			this.dosage = (summary.dosages || []).find(x => x.appointmentId === this.appointmentId);

			if (this.showStructurePicker)
				this.structureId = this.maintenanceReading?.structureId ?? this.dosage?.structureId ?? this.structures[0]?.id ?? null;
		}
		else {
			this.maintenanceReading = null;
			this.dosage = null;
		}

		this.readingRows = readingItems.map(item => {
			const existing = this.maintenanceReading?.maintenanceReadingValues?.find(v => v.maintenanceReadingItemId === item.maintenanceReadingItemId);
			return {
				maintenanceReadingItemId: item.maintenanceReadingItemId,
				itemName: item.itemName,
				uom: item.uom,
				values: (item.itemValues || []).filter(v => v.isActive).sort((a, b) => a.sortOrder - b.sortOrder).map(v => v.value),
				itemValue: existing ? existing.itemValue : ""
			};
		});

		this.dosageRows = dosageItems.map(item => {
			const existing = this.dosage?.dosageValues?.find(v => v.dosageItemId === item.dosageItemId);
			return {
				dosageItemId: item.dosageItemId,
				description: item.description,
				uom: item.uom,
				values: (item.itemValues || []).filter(v => v.isActive).sort((a, b) => a.sortOrder - b.sortOrder).map(v => v.value),
				value: existing ? existing.value : null,
				addToInvoice: existing ? existing.addToInvoice : false,
				hasItem: !!item.itemId
			};
		});

		this.dialogRef.showDialog();
	}

	async save() {
		this.spinnerStatus = "spin";

		try {
			await this.saveReadings();
			await this.saveDosages();
			this.spinnerStatus = "ok";
			this.onSaved.emit();
			this.dialogRef.hideDialog();
		}
		catch {
			this.spinnerStatus = "error";
		}
	}

	getStructureTypeName(structureTypeId: number): string {
		return this.structureTypes?.find(t => t.id === structureTypeId)?.name ?? "Structure";
	}

	private async saveReadings() {
		const enteredRows = this.readingRows.filter(r => r.itemValue !== null && r.itemValue !== undefined && r.itemValue.toString().trim() !== "");

		// Nothing entered and nothing previously saved — leave it alone.
		if (enteredRows.length === 0 && !this.maintenanceReading)
			return;

		const model: IMaintenanceReadingModel = this.maintenanceReading || new MaintenanceReadingModel();
		model.customerId = this.customerId;
		model.appointmentId = this.appointmentId;
		model.structureId = this.structureId;
		if (!model.maintenanceReadingDate)
			model.maintenanceReadingDate = new Date();

		model.maintenanceReadingValues = enteredRows.map(r => {
			const v = new MaintenanceReadingValueModel();
			v.maintenanceReadingId = model.maintenanceReadingId;
			v.maintenanceReadingItemId = r.maintenanceReadingItemId;
			v.itemName = r.itemName;
			v.itemValue = r.itemValue.toString().trim();
			return v;
		});

		if (model.maintenanceReadingId === 0)
			this.maintenanceReading = await this.maintenanceHistoryService.addMaintenanceReading(model);
		else
			this.maintenanceReading = await this.maintenanceHistoryService.updateMaintenanceReading(model);
	}

	private async saveDosages() {
		const enteredRows = this.dosageRows.filter(r => r.value !== null && r.value !== undefined && !isNaN(Number(r.value)));

		if (enteredRows.length === 0 && !this.dosage)
			return;

		const model: IDosageModel = this.dosage || new DosageModel();
		model.customerId = this.customerId;
		model.appointmentId = this.appointmentId;
		model.structureId = this.structureId;
		if (!model.dosageDate)
			model.dosageDate = new Date();

		model.dosageValues = enteredRows.map(r => {
			const v = new DosageValueModel();
			v.dosageId = model.dosageId;
			v.dosageItemId = r.dosageItemId;
			v.description = r.description;
			v.uom = r.uom;
			v.value = Number(r.value);
			v.addToInvoice = r.addToInvoice;
			return v;
		});

		if (model.dosageId === 0)
			this.dosage = await this.maintenanceHistoryService.addDosage(model);
		else
			this.dosage = await this.maintenanceHistoryService.updateDosage(model);
	}

	openPanel(kind: "reading" | "dosage", index: number) {
		// Cancel any pending close from the previously focused input, otherwise moving from
		// one input straight to another would close the panel we just opened.
		if (this.closePanelTimeout) {
			clearTimeout(this.closePanelTimeout);
			this.closePanelTimeout = null;
		}
		this.activePanel = { kind, index };
	}

	isPanelOpen(kind: "reading" | "dosage", index: number): boolean {
		return this.activePanel?.kind === kind && this.activePanel?.index === index;
	}

	closePanelSoon() {
		// Delay so a (mousedown) on a suggestion is registered before the panel is removed.
		this.closePanelTimeout = setTimeout(() => {
			this.activePanel = null;
			this.closePanelTimeout = null;
		}, 150);
	}

	// Filters the item's configured values by what's currently typed (substring match),
	// mirroring how the native datalist narrowed the list.
	matchValues(values: number[], current: string | number): number[] {
		const text = (current === null || current === undefined) ? "" : current.toString().trim();
		if (text === "")
			return values;

		// If the current text exactly matches a configured value, the user already has one
		// selected — show the full list so they can switch rather than only seeing that value.
		if (values.some(v => v.toString() === text))
			return values;

		return values.filter(v => v.toString().includes(text));
	}

	selectValue(kind: "reading" | "dosage", row: IReadingRow | IDosageRow, value: number) {
		if (kind === "reading")
			(row as IReadingRow).itemValue = value.toString();
		else
			(row as IDosageRow).value = value;

		this.activePanel = null;
	}

	cancel() {
		this.dialogRef.hideDialog();
	}
}
